import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-meetings',
  templateUrl: './meetings.component.html',
  styleUrls: ['./meetings.component.scss']
})
export class MeetingsComponent implements OnInit {
  AppConstants = AppConstants;
  meetings: any[] = [];
  tableColumns: TableColumn[] = [];
  private isFetching: boolean = false;

  constructor(
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private _router: Router,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.initializeTableColumns();
    // Prevent duplicate calls if component is initialized multiple times
    if (!this.isFetching) {
      this.fetchMeetings();
    }
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        iconPath: 'assets/icons/edit-icon.svg',
        action: 'edit',
        title: 'Edit'
      }
    ];

    this.tableColumns = [
      {
        field: 'meetingId',
        header: 'Meeting ID',
        type: 'text'
      },
      {
        field: 'title',
        header: 'Title',
        type: 'text'
      },
      {
        field: 'dateTime',
        header: 'Date & Time',
        type: 'text'
      },
      {
        field: 'agendaCount',
        header: 'Agenda Count',
        type: 'number'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'mom',
        header: 'MOM',
        type: 'link'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: actionIcons
      }
    ];
  }

  async fetchMeetings(): Promise<void> {
    // Prevent duplicate calls
    if (this.isFetching) {
      console.log('fetchMeetings already in progress, skipping duplicate call');
      return;
    }
    
    this.isFetching = true;
    // Ensure loader is shown before starting
    this._loaderService.showLoader();
    
    try {
      const url = `${environment.API_URL}/api/Meetings`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        // Sort meetings: first by status priority (OnGoing > WaitingForAttendance > others), then by Date & Time (newest first)
        const sortedData = [...response.data].sort((a: any, b: any) => {
          // Get status values (normalize to lowercase for comparison)
          const statusA = (a.statusName || a.status || '').toString().toLowerCase();
          const statusB = (b.statusName || b.status || '').toString().toLowerCase();
          
          // Define status priority: OnGoing (3) > WaitingForAttendance (2) > others
          const getStatusPriority = (status: string): number => {
            if (status === 'ongoing' || status === '3') return 1; // Highest priority
            if (status === 'waitingforattendance' || status === '2') return 2; // Second priority
            return 3; // All other statuses
          };
          
          const priorityA = getStatusPriority(statusA);
          const priorityB = getStatusPriority(statusB);
          
          // First sort by status priority
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          
          // If same priority, sort by Date & Time (newest first - descending order)
          const dateA = a.dateTime ? new Date(a.dateTime).getTime() : 0;
          const dateB = b.dateTime ? new Date(b.dateTime).getTime() : 0;
          return dateB - dateA;
        });

        // Map backend response to table format
        // Process data synchronously to ensure loader stays visible during processing
        this.meetings = sortedData.map((meeting: any) => {
          const meetingDate = new Date(meeting.dateTime);
          const formattedDateTime = meetingDate.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(',', '');

          // Use status from API (already calculated in backend)
          const status = meeting.statusName || meeting.status || 'Draft';

          return {
            id: meeting.id,
            meetingId: meeting.meetingId,
            title: meeting.meetingTitle,
            dateTime: formattedDateTime,
            agendaCount: meeting.agendaCount || 0,
            status: status,
            statusValue: meeting.status, // Store original status value for edit check
            mom: 'Download',
            location: meeting.location,
            chairmanName: meeting.chairmanName
          };
        });
        // Trigger change detection to ensure data is rendered
        this.cdr.markForCheck();
      } else {
        console.error('Failed to fetch meetings:', response?.errorMessageEn);
        this.meetings = [];
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
      this.meetings = [];
      this.cdr.markForCheck();
    } finally {
      this.isFetching = false;
      // Use setTimeout to ensure UI has time to render the data before hiding loader
      // This prevents the loader from closing before the data is visible
      setTimeout(() => {
        this._loaderService.hideLoader();
        this.cdr.markForCheck();
      }, 100);
    }
  }

  createMeeting(): void {
    this._router.navigate([AppConstants.ROUTES.MEETINGS, AppConstants.ROUTES.CREATE_MEETING]);
  }

  uploadMeetings(): void {
    // TODO: Implement upload functionality
    console.log('Upload Meetings');
  }

  onTableActionClick(event: {action: string, row: any}): void {
    switch(event.action) {
      case 'view':
        this.viewMeeting(event.row.meetingId);
        break;
      case 'edit':
        this.editMeeting(event.row);
        break;
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'mom') {
      this.downloadMOM(event.row.meetingId);
    }
  }

  viewMeeting(meetingId: string | number): void {
    // If meetingId is a number (from backend), use it directly, otherwise use the string
    const id = typeof meetingId === 'number' ? meetingId : meetingId;
    this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}/${AppConstants.ROUTES.VIEW_MEETING}/${id}`]);
  }

  editMeeting(row: any): void {
    // Check if meeting status is Draft - only allow editing Draft meetings
    const status = row.status || row.statusName || '';
    const statusLower = status.toString().toLowerCase();
    const isDraft = statusLower === 'draft' || statusLower === '1' || status === 1;
    
    if (!isDraft) {
      // Show error message - can't edit non-draft meetings
      this.toasterService.showError('Only meetings with Draft status can be edited.');
      return;
    }
    
    // Navigate to create-meeting page with meeting ID for editing
    this._router.navigate([AppConstants.ROUTES.MEETINGS, AppConstants.ROUTES.CREATE_MEETING], {
      queryParams: { id: row.meetingId, edit: true }
    });
  }

  downloadMOM(meetingId: string): void {
    // TODO: Download MOM document
    console.log('Download MOM:', meetingId);
  }
}

